import {
    AfterViewInit,
    Directive,
    DoCheck,
    ElementRef,
    HostBinding,
    inject,
    input,
    Renderer2,
} from '@angular/core';

/**
 * Diretiva de área de texto da biblioteca.
 *
 * Aplica o visual padrão (cantos arredondados) ao `<textarea>` e, opcionalmente:
 * - exibe um contador de caracteres `atual/máximo` (ex.: `21/50`) no canto
 *   inferior direito — espelhando o `[showCharacterCount]` do `ai-text-input`;
 * - cresce a altura automaticamente conforme o número de linhas digitadas
 *   (`[autoGrow]`), até um limite de `[maxRows]` linhas, com transição suave.
 *
 * @example
 * ```html
 * <textarea aiTextarea [maxLength]="250" [showCharacterCount]="true"></textarea>
 * <textarea aiTextarea [autoGrow]="true" [maxRows]="10"></textarea>
 * ```
 */
@Directive({
    selector: '[aiTextarea]',
})
export class TextArea implements AfterViewInit, DoCheck {
    @HostBinding('style.border-radius')
    borderRadius = 'var(--overlay-rounder)';

    /**
     * Exibe um contador `atual/máximo` no canto inferior direito da área de
     * texto. Requer o atributo `maxlength` (`[maxLength]`) definido.
     */
    readonly showCharacterCount = input<boolean>(false);

    /**
     * Faz a área de texto crescer verticalmente conforme o conteúdo ganha
     * linhas (em vez de manter altura fixa com rolagem interna). A cada nova
     * linha a altura aumenta, animada por uma transição suave, até o limite de
     * {@link maxRows}; a partir daí surge a rolagem interna.
     */
    readonly autoGrow = input<boolean>(false);

    /**
     * Número máximo de linhas que a área de texto ocupa ao crescer com
     * {@link autoGrow}. Ao ultrapassar esse limite a altura para de crescer e
     * o conteúdo passa a rolar internamente. Ignorado quando `autoGrow` é falso.
     */
    readonly maxRows = input<number>(10);

    private readonly host = inject<ElementRef<HTMLTextAreaElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

    /** Elemento do contador (criado apenas quando habilitado). */
    private counterEl?: HTMLElement;
    /** Último texto renderizado — evita escritas desnecessárias no DOM. */
    private lastText = '';
    /** Último valor para o qual a altura foi recalculada (auto-grow). */
    private lastResizedValue: string | null = null;

    ngAfterViewInit(): void {
        const textarea = this.host.nativeElement;

        if (this.autoGrow()) {
            this.setupAutoGrow(textarea);
        }

        // Igual ao `ai-text-input`: contador só quando habilitado E com maxlength.
        if (!this.showCharacterCount() || textarea.maxLength <= 0) {
            return;
        }
        this.buildCounter(textarea);
        this.refresh(textarea);
    }

    ngDoCheck(): void {
        // Recalcula a altura sempre que o valor muda (digitação, colagem ou
        // limpeza programática, ex.: após o envio da mensagem).
        if (this.autoGrow() && this.host.nativeElement.value !== this.lastResizedValue) {
            this.resize(this.host.nativeElement);
        }

        // Reativo a qualquer mudança de valor (usuário ou programática), como o
        // getter `currentLength` do `ai-text-input`.
        if (this.counterEl) {
            this.refresh(this.host.nativeElement);
        }
    }

    /**
     * Prepara a área de texto para o crescimento automático: caixa em
     * `border-box` (para a altura em px casar com o `scrollHeight`), rolagem
     * interna oculta por padrão e transição de altura para animar o efeito.
     */
    private setupAutoGrow(textarea: HTMLTextAreaElement): void {
        const r = this.renderer;
        r.setStyle(textarea, 'box-sizing', 'border-box');
        r.setStyle(textarea, 'overflow-y', 'hidden');
        r.setStyle(textarea, 'transition', 'height 0.15s ease');
        this.resize(textarea);
    }

    /**
     * Ajusta a altura da área de texto ao conteúdo atual, limitada a
     * {@link maxRows} linhas. Zera a altura para medir o `scrollHeight` real e,
     * ao ultrapassar o limite, habilita a rolagem interna.
     */
    private resize(textarea: HTMLTextAreaElement): void {
        this.lastResizedValue = textarea.value;

        const r = this.renderer;
        // Zera a altura para que o `scrollHeight` reflita o conteúdo (e não a
        // altura anterior já aplicada).
        r.setStyle(textarea, 'height', 'auto');

        const maxHeight = this.computeMaxHeight(textarea);
        const contentHeight = textarea.scrollHeight;
        const nextHeight = Math.min(contentHeight, maxHeight);

        r.setStyle(textarea, 'height', `${nextHeight}px`);
        r.setStyle(
            textarea,
            'overflow-y',
            contentHeight > maxHeight ? 'auto' : 'hidden',
        );
    }

    /**
     * Calcula a altura máxima (em px) equivalente a {@link maxRows} linhas,
     * somando a altura de linha, os paddings e as bordas verticais.
     */
    private computeMaxHeight(textarea: HTMLTextAreaElement): number {
        const cs = getComputedStyle(textarea);
        const fontSize = parseFloat(cs.fontSize) || 16;
        let lineHeight = parseFloat(cs.lineHeight);
        if (Number.isNaN(lineHeight)) {
            // `line-height: normal` não é numérico — aproxima por 1.2× a fonte.
            lineHeight = fontSize * 1.2;
        }
        const paddingTop = parseFloat(cs.paddingTop) || 0;
        const paddingBottom = parseFloat(cs.paddingBottom) || 0;
        const borderTop = parseFloat(cs.borderTopWidth) || 0;
        const borderBottom = parseFloat(cs.borderBottomWidth) || 0;

        return (
            lineHeight * this.maxRows() +
            paddingTop +
            paddingBottom +
            borderTop +
            borderBottom
        );
    }

    /** Envolve o textarea e cria o elemento do contador posicionado. */
    private buildCounter(textarea: HTMLTextAreaElement): void {
        const r = this.renderer;
        const parent = r.parentNode(textarea);

        // Wrapper `position: relative` para ancorar o contador ao textarea.
        const wrapper = r.createElement('span');
        r.setStyle(wrapper, 'position', 'relative');
        r.setStyle(wrapper, 'display', 'block');
        r.setStyle(wrapper, 'width', '100%');
        r.insertBefore(parent, wrapper, textarea);
        r.appendChild(wrapper, textarea);

        // Reserva espaço embaixo para o contador não cobrir a última linha.
        r.setStyle(textarea, 'padding-bottom', '26px');

        const counter = r.createElement('span');
        r.setStyle(counter, 'position', 'absolute');
        r.setStyle(counter, 'right', '12px');
        r.setStyle(counter, 'bottom', '10px');
        r.setStyle(counter, 'font-size', '11px');
        r.setStyle(counter, 'font-variant-numeric', 'tabular-nums');
        r.setStyle(counter, 'color', 'var(--color-text-muted)');
        r.setStyle(counter, 'pointer-events', 'none');
        r.appendChild(wrapper, counter);

        this.counterEl = counter;
    }

    /** Atualiza o texto do contador (`atual/máximo`) se ele tiver mudado. */
    private refresh(textarea: HTMLTextAreaElement): void {
        const length = textarea.value?.length ?? 0;
        const text = `${length}/${textarea.maxLength}`;
        if (text !== this.lastText) {
            this.lastText = text;
            this.renderer.setProperty(this.counterEl, 'textContent', text);
        }
    }
}
